import { CheckCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  GET_COMPANY,
  GET_INVESTOR_INSIGHTS,
  InvestorInsights,
  UPDATE_INVESTOR_INSIGHTS,
} from '@frontend/data-access';
import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

import ArrayInput from './edit-actions/array-input';

const { Text } = Typography;

enum MyTypes {
  string,
  int,
  array,
  date,
  funding,
}

const variableTypes: { [key: string]: MyTypes } = {
  information: MyTypes.string,
  partners: MyTypes.array,
  customerAmount: MyTypes.int,
  totalTeamSize: MyTypes.int,
  techEmployees: MyTypes.int,
  salesEmployees: MyTypes.int,
  revenuePlan: MyTypes.string,
  totalAmountRaised: MyTypes.int,
  currentInvestors: MyTypes.array,
  targetFundRaisingAmount: MyTypes.string,
  fundingType: MyTypes.funding, // SAFE CONVERTIBLE EQUITY
  fundUsage: MyTypes.string,
  closingDate: MyTypes.date,
  approvals: MyTypes.string,
};

export type InsightItemType = {
  title: string;
  items: string[];
};

interface InsightCardProps {
  item: InsightItemType;
  data: InvestorInsights;
}

const EditInsightsCard = ({ item, data }: InsightCardProps) => {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [isEditing, setIsEditing] = useState(false);
  const router = useRouter();
  const [form] = useForm();
  const id = Number(router.query['companyId']);
  const [customValues, setCustomValues] = useState({}); // Array values

  const keyToLabel: { [key: string]: string } = useMemo(() => {
    return {
      information: t('company_profile_information'),
      partners: t('company_profile_partners'),
      customerAmount: t('company_profile_customerAmount'),
      totalTeamSize: t('company_profile_totalTeamSize'),
      techEmployees: t('company_profile_techEmployees'),
      salesEmployees: t('company_profile_salesEmployees'),
      revenuePlan: t('company_profile_revenuePlan'),
      totalAmountRaised: t('company_profile_totalAmountRaised'),
      currentInvestors: t('company_profile_currentInvestors'),
      targetFundRaisingAmount: t('company_profile_targetFundRaisingAmount'),
      fundingType: t('company_profile_fundingType'),
      fundUsage: t('company_profile_fundUsage'),
      closingDate: t('company_profile_closingDate'),
      approvals: t('company_profile_approvals'),
    };
  }, [t]);
  const { data: dataCompany } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: id },
  });

  const [update, { loading }] = useMutation(UPDATE_INVESTOR_INSIGHTS);

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      closingDate: data?.closingDate ? dayjs(data?.closingDate) : null,
    });
  }, [data, form]);

  const onSave = (values: InvestorInsights) => {
    const input: InvestorInsights = {
      ...values,
      ...customValues,
      companyId: id,
    };
    if (values.closingDate) {
      input.closingDate = dayjs(values.closingDate).format(
        'YYYY-MM-DDTHH:mm:ss.SSSZ'
      );
    }
    update({
      variables: {
        input: input,
      },
      refetchQueries: [
        { query: GET_INVESTOR_INSIGHTS, variables: { companyId: id } },
      ],
      onCompleted() {
        setIsEditing(false);
        notification.open({
          message: t('success_message'),
          icon: (
            <span className="text-green-600">
              <CheckCircleOutlined />
            </span>
          ),
        });
      },
    });
  };

  const renderValue = (key: string) => {
    const type = variableTypes[key];
    const value = (data as any)?.[key];
    if (!value) {
      return <div />;
    }
    let element: React.ReactNode;
    switch (type) {
      case MyTypes.array:
        element = (
          <div className="flex flex-col">
            {value.map((v: string, index: number) => {
              return (
                <Text>
                  {index + 1}. {v}
                </Text>
              );
            })}
          </div>
        );
        break;
      case MyTypes.date:
        element = <Text>{dayjs(value).format('YYYY.MM.DD')}</Text>;
        break;
      case MyTypes.funding:
      case MyTypes.string:
      case MyTypes.int:
      default:
        element = <Text>{value}</Text>;
        break;
    }
    return <div className="py-5 flex items-center h-full">{element}</div>;
  };

  const renderInput = (key: string) => {
    const type = variableTypes[key];
    let element: React.ReactNode = null;
    switch (type) {
      case MyTypes.array:
        element = (
          <ArrayInput
            id={key}
            value={(data as any)?.[key]}
            onChange={(val) => {
              setCustomValues({
                ...customValues,
                [key]: val,
              });
            }}
          />
        );
        break;
      case MyTypes.date:
        element = (
          <Form.Item name={key} noStyle>
            <DatePicker data-test={`${key}-input`} />
          </Form.Item>
        );
        break;
      case MyTypes.funding:
        element = (
          <Form.Item name={key} noStyle>
            <Select
              className="w-full"
              options={[
                { value: 'SAFE', label: t('safe') },
                { value: 'CONVERTIBLE', label: t('convertible') },
                { value: 'EQUITY', label: t('equity') },
              ]}
              data-test={`${key}-select`}
            />
          </Form.Item>
        );
        break;
      case MyTypes.string:
        element = (
          <Form.Item name={key} noStyle>
            <Input max={500} data-test={`${key}-input`} maxLength={255} />
          </Form.Item>
        );
        break;
      case MyTypes.int:
        element = (
          <Form.Item name={key} noStyle>
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              precision={0}
              min={1}
              maxLength={9}
              data-test={`${key}-input`}
            />
          </Form.Item>
        );
        break;
      default:
        element = <Text>No input</Text>;
        break;
    }
    return (
      <div className="py-5 pr-5 flex items-center h-full w-full">{element}</div>
    );
  };

  return (
    <div className="flex justify-between" data-test="insight-card">
      <div className="flex-1 grid">
        <div className="flex gap-2 justify-between">
          <span className="text-primary-600 text-sm font-semibold mb-2">
            {item.title.toUpperCase()}
          </span>
          {dataCompany?.company.isOwner && (
            <div className="flex">
              {isEditing ? (
                <div>
                  <Button
                    danger
                    type="link"
                    disabled={loading}
                    onClick={() => {
                      setIsEditing(false);
                    }}
                    data-test="insight-cancel-btn"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="link"
                    loading={loading}
                    onClick={() => {
                      form.submit();
                    }}
                    data-test="insight-save-btn"
                  >
                    {t('save')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="link"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  data-test="insight-edit-btn"
                >
                  {t('edit')}
                </Button>
              )}
            </div>
          )}
        </div>
        <Form form={form} onFinish={onSave}>
          <div className="relative overflow-x-auto sm:rounded-lg">
            <div id="detailed-pricing" className="w-full overflow-x-auto">
              <div className="overflow-hidden">
                {item.items.map((key) => {
                  return (
                    <div className="grid grid-cols-2 text-sm text-gray-700 border-b border-gray-400 gap-x-16">
                      <div className="text-gray-900 p-5 bg-gray-200">
                        {keyToLabel[key]}
                      </div>
                      {isEditing ? (
                        <div>{renderInput(key)}</div>
                      ) : (
                        <span className="text-gray-900">
                          {renderValue(key)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EditInsightsCard;
